import { useEffect, useState, type FormEvent, type ReactNode } from "react";
import {
  abandonPlatformMfa,
  cancelPlatformMfaEnrollment,
  PlatformAuthError,
  requestPlatformCode,
  signOutPlatform,
  verifyPlatformAuthenticator,
  verifyPlatformCode,
  type PlatformMfaPrompt
} from "./admin-auth";
import { AdminApiError, loadBootstrap, loadPlatformReadiness } from "./admin-api";
import {
  hasPermission,
  parseAdminRoute,
  permissionForRoute,
  routeHash,
  type AdminBootstrap,
  type AdminRoute,
  type PlatformPermission,
  type PlatformReadiness
} from "./admin-model";
import {
  AccessUnavailable,
  AuditPage,
  CustomerDetailPage,
  CustomersPage,
  HealthPage,
  LabsPage,
  NotFoundPage,
  OverviewPage,
  ReleasesPage,
  SupportPage,
  ValidationPage
} from "./SuperAdminPages";
import { MartinProductLab } from "./MartinProductLab";
import { PlatformMfa } from "./PlatformMfa";
import "./truth.css";
import "./lab.css";
import "./super-admin.css";

type Navigate = (route: Exclude<AdminRoute, { page: "not_found" }>) => void;

function Login({ onDone }: Readonly<{ onDone: () => void }>) {
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [step, setStep] = useState<"email" | "code">("email");
  const [mfaPrompt, setMfaPrompt] = useState<PlatformMfaPrompt>();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const submit = async (event: FormEvent) => {
    event.preventDefault(); setBusy(true); setError(undefined);
    try {
      if (step === "email") { await requestPlatformCode(email.trim()); setStep("code"); }
      else {
        const prompt = await verifyPlatformCode(email.trim(), code);
        if (prompt === undefined) { setCode(""); onDone(); }
        else setMfaPrompt(prompt);
      }
    } catch (reason) {
      if (reason instanceof PlatformAuthError && reason.restartAtEmail) {
        setStep("email"); setCode(""); setMfaPrompt(undefined);
      }
      setError(reason instanceof Error ? reason.message : "Sign-in failed.");
    }
    finally { setBusy(false); }
  };
  const verifyMfa = async (mfaCode: string) => {
    if (mfaPrompt === undefined || busy) return;
    setBusy(true); setError(undefined);
    try {
      await verifyPlatformAuthenticator(mfaPrompt.factorId, mfaCode);
      setMfaPrompt(undefined); setCode(""); setStep("email");
      onDone();
    }
    catch (reason) {
      if (reason instanceof PlatformAuthError && reason.restartAtEmail) {
        setStep("email"); setCode(""); setMfaPrompt(undefined);
      }
      setError(reason instanceof Error ? reason.message : "Authenticator verification failed.");
    } finally { setBusy(false); }
  };
  const cancelMfa = async () => {
    if (mfaPrompt === undefined || busy) return;
    setBusy(true); setError(undefined);
    try {
      if (mfaPrompt.kind === "enroll") await cancelPlatformMfaEnrollment(mfaPrompt.factorId);
      else await abandonPlatformMfa();
      setStep("email"); setCode(""); setMfaPrompt(undefined);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Authenticator setup could not be cancelled safely.");
    } finally { setBusy(false); }
  };
  return <main className="login"><section className="login-card"><div className="brand-mark">P</div>{mfaPrompt === undefined ? <><p className="eyebrow">Phenera Platform</p><h1>Owner access</h1><p>{step === "email" ? "Sign in to the control-plane assurance workspace. Platform authority does not confer customer-data access." : `Enter the code sent to ${email}.`}</p><form data-control-id="admin.auth.submit-form" onSubmit={submit}>{step === "email" ? <label><span>Email</span><input data-control-id="admin.auth.email" type="email" required value={email} onChange={(event) => setEmail(event.target.value)} /></label> : <label><span>Sign-in code</span><input data-control-id="admin.auth.otp" inputMode="numeric" autoComplete="one-time-code" pattern="[0-9]{6,10}" maxLength={10} required value={code} onChange={(event) => setCode(event.target.value.replace(/\D/gu, ""))} /></label>}{error !== undefined && <p className="error" role="alert">{error}</p>}<button data-control-id="admin.auth.submit" className="primary" disabled={busy} title={busy ? "Wait for the current sign-in request to finish." : undefined}>{busy ? "Please wait…" : step === "email" ? "Send sign-in code" : "Continue securely"}</button></form></> : <PlatformMfa prompt={mfaPrompt} busy={busy} {...(error === undefined ? {} : { error })} onVerify={(value) => void verifyMfa(value)} onCancel={() => void cancelMfa()} />}</section></main>;
}

type NavItemProps = Readonly<{
  active: boolean;
  "data-control-id": string;
  label: string;
  symbol: string;
  permission: PlatformPermission;
  owner: AdminBootstrap["owner"];
  onClick: () => void;
}>;

function NavItem({ active, "data-control-id": controlId, label, symbol, permission, owner, onClick }: NavItemProps) {
  const allowed = hasPermission(owner, permission);
  const reason = allowed ? undefined : `${permission} is required for this workspace.`;
  return <button data-control-id={controlId} className={active ? "active" : ""} disabled={!allowed} title={reason} onClick={onClick}><span aria-hidden="true">{symbol}</span><span>{label}</span>{!allowed && <small>Unavailable</small>}</button>;
}

function Shell({ data, route, navigate, children }: Readonly<{
  data: AdminBootstrap;
  route: AdminRoute;
  navigate: Navigate;
  children: ReactNode;
}>) {
  const signOut = async () => { await signOutPlatform(); location.hash = "#/overview"; location.reload(); };
  return <div className="app owner-console"><aside><div className="brand"><span>P</span><div><strong>Phenera</strong><small>Platform Owner</small></div></div><nav aria-label="Owner console">
    <NavItem data-control-id="admin.nav.overview" label="Overview" symbol="⌂" permission="PERM-001-01" owner={data.owner} active={route.page === "overview"} onClick={() => navigate({ page: "overview" })} />
    <NavItem data-control-id="admin.nav.customers" label="Customers" symbol="◦" permission="PERM-001-01" owner={data.owner} active={route.page === "customers" || route.page === "customer"} onClick={() => navigate({ page: "customers" })} />
    <NavItem data-control-id="admin.nav.product-labs" label="Product Labs" symbol="≋" permission="PERM-001-02" owner={data.owner} active={route.page === "labs" || route.page === "lab"} onClick={() => navigate({ page: "labs" })} />
    <NavItem data-control-id="admin.nav.validation" label="Validation" symbol="✓" permission="PERM-001-01" owner={data.owner} active={route.page === "validation"} onClick={() => navigate({ page: "validation" })} />
    <NavItem data-control-id="admin.nav.releases" label="Release pins" symbol="◎" permission="PERM-001-02" owner={data.owner} active={route.page === "releases"} onClick={() => navigate({ page: "releases" })} />
    <NavItem data-control-id="admin.nav.support" label="Human Observation" symbol="◇" permission="PERM-001-03" owner={data.owner} active={route.page === "support"} onClick={() => navigate({ page: "support" })} />
    <NavItem data-control-id="admin.nav.audit" label="Audit" symbol="↯" permission="PERM-001-01" owner={data.owner} active={route.page === "audit"} onClick={() => navigate({ page: "audit" })} />
    <NavItem data-control-id="admin.nav.health" label="Health" symbol="●" permission="PERM-001-01" owner={data.owner} active={route.page === "health"} onClick={() => navigate({ page: "health" })} />
  </nav><footer><span className="avatar">RB</span><div><strong>{data.owner.displayName}</strong><small>{data.owner.email}</small><em>{data.owner.role.replaceAll("_", " ")}</em></div><button data-control-id="admin.auth.sign-out" aria-label="Sign out" title="Sign out" onClick={() => void signOut()}>↪</button></footer></aside><main className="content">{children}</main></div>;
}

export function AdminApp() {
  const [data, setData] = useState<AdminBootstrap>();
  const [readiness, setReadiness] = useState<PlatformReadiness>();
  const [authRequired, setAuthRequired] = useState(false);
  const [error, setError] = useState<string>();
  const [route, setRoute] = useState<AdminRoute>(() => parseAdminRoute(location.hash));

  const navigate: Navigate = (next) => { location.hash = routeHash(next); };
  const reload = () => {
    setError(undefined);
    void loadBootstrap().then((value) => {
      setData(value); setAuthRequired(false);
      if (location.hash === "" || parseAdminRoute(location.hash).page === "not_found") location.hash = "#/overview";
    }).catch((reason: unknown) => {
      if (reason instanceof AdminApiError && reason.status === 401) setAuthRequired(true);
      else setError(reason instanceof Error ? reason.message : "The platform owner console is unavailable.");
    });
    void loadPlatformReadiness().then(setReadiness).catch(() => setReadiness(undefined));
  };

  useEffect(reload, []);
  useEffect(() => {
    const update = () => setRoute(parseAdminRoute(location.hash));
    addEventListener("hashchange", update);
    return () => removeEventListener("hashchange", update);
  }, []);

  if (authRequired) return <Login onDone={reload} />;
  if (error !== undefined) return <main className="fatal"><h1>Platform unavailable</h1><p>{error}</p><button data-control-id="admin.shell.retry" className="primary" onClick={reload}>Try again</button></main>;
  if (data === undefined) return <main className="loading">Establishing the platform-owner assurance workspace…</main>;

  const requiredPermission = permissionForRoute(route);
  const permitted = requiredPermission === undefined || hasPermission(data.owner, requiredPermission);
  let page: ReactNode;
  if (!permitted && requiredPermission !== undefined) page = <AccessUnavailable permission={requiredPermission} />;
  else {
    switch (route.page) {
      case "overview": page = <OverviewPage data={data} navigate={navigate} {...(readiness === undefined ? {} : { readiness })} />; break;
      case "customers": page = <CustomersPage data={data} navigate={navigate} />; break;
      case "customer": page = <CustomerDetailPage customerKey={route.customerKey} navigate={navigate} />; break;
      case "labs": page = <LabsPage data={data} navigate={navigate} />; break;
      case "lab": {
        const customer = data.customers.find((candidate) => candidate.key === route.customerKey);
        page = customer === undefined
          ? <NotFoundPage navigate={navigate} />
          : <MartinProductLab customerKey={customer.key} customerName={customer.name} operatorName={data.owner.displayName} back={() => navigate({ page: "labs" })} />;
        break;
      }
      case "validation": page = <ValidationPage data={data} navigate={navigate} />; break;
      case "releases": page = <ReleasesPage data={data} navigate={navigate} />; break;
      case "support": page = <SupportPage />; break;
      case "audit": page = <AuditPage />; break;
      case "health": page = <HealthPage data={data} />; break;
      case "not_found": page = <NotFoundPage navigate={navigate} />; break;
    }
  }
  return <Shell data={data} route={route} navigate={navigate}>{page}</Shell>;
}
